<template>
  <main class="talk">
    <div class="talk__inner">
      <button type="button" class="talk__back" @click="goBack">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <path d="M5 12H19" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M13 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <span>بازگشت</span>
      </button>

      <header class="talk__header">
        <p class="talk__eyebrow">جامعه یادگیری</p>
        <h1 class="talk__title">صحبت کنیم</h1>
        <p class="talk__subtitle">
          سؤال بپرس، تجربه رد و بدل کن، و به بقیه انگیزه بده.
        </p>
      </header>

      <form class="talk__composer" @submit.prevent="submitPost">
        <textarea
          v-model="newPost"
          rows="3"
          maxlength="4000"
          placeholder="چی تو ذهنته؟ اینجا بنویس..."
          required
        ></textarea>
        <div class="talk__composer-actions">
          <button type="submit" class="talk__btn talk__btn--primary" :disabled="posting || !newPost.trim()">
            {{ posting ? "در حال ارسال..." : "ارسال نظر" }}
          </button>
        </div>
      </form>

      <p v-if="formError" class="talk__error" role="alert">{{ formError }}</p>

      <LoadingSpinner v-if="loading" message="در حال بارگذاری گفتگوها..." />

      <ErrorMessage
        v-else-if="errorMessage"
        :message="errorMessage"
        @retry="load"
      />

      <EmptyState
        v-else-if="!posts.length"
        title="هنوز حرفی زده نشده"
        message="اولین نفر باش و گفتگو رو شروع کن."
      />

      <section v-else class="talk__feed" aria-label="نظرات">
        <article v-for="post in posts" :key="post.id" class="talk__card">
          <header class="talk__card-head">
            <div
              class="talk__avatar"
              :class="{ 'talk__avatar--image': !!authorAvatar(post.author) }"
              :style="authorAvatar(post.author) ? undefined : avatarStyle(post.author.id)"
            >
              <img
                v-if="authorAvatar(post.author)"
                :src="authorAvatar(post.author)!"
                alt=""
                class="talk__avatar-img"
              />
              <template v-else>{{ initials(post.author) }}</template>
            </div>
            <div class="talk__meta">
              <strong>{{ displayName(post.author) }}</strong>
              <time>{{ formatTime(post.created_at) }}</time>
            </div>
          </header>

          <p class="talk__body">{{ post.body }}</p>

          <div class="talk__actions">
            <button
              type="button"
              class="talk__action"
              :class="{ 'talk__action--active': post.liked_by_me }"
              @click="likePost(post)"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" :fill="post.liked_by_me ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              {{ toPersianDigits(post.like_count) }}
            </button>
            <button
              type="button"
              class="talk__action"
              :class="{ 'talk__action--open': expanded[post.id] }"
              :aria-expanded="!!expanded[post.id]"
              @click="toggleReplies(post.id)"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path d="M21 15a4 4 0 0 1-4 4H7l-4 4V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <template v-if="expanded[post.id]">مخفی کردن پاسخ‌ها</template>
              <template v-else>
                {{
                  post.reply_count > 0
                    ? `نمایش پاسخ‌ها (${toPersianDigits(post.reply_count)})`
                    : "پاسخ بده"
                }}
              </template>
            </button>
          </div>

          <div v-show="expanded[post.id]" class="talk__replies">
            <div
              v-for="reply in post.replies"
              :key="reply.id"
              class="talk__reply"
            >
              <div class="talk__reply-head">
                <div
                  class="talk__avatar talk__avatar--sm"
                  :class="{ 'talk__avatar--image': !!authorAvatar(reply.author) }"
                  :style="authorAvatar(reply.author) ? undefined : avatarStyle(reply.author.id)"
                >
                  <img
                    v-if="authorAvatar(reply.author)"
                    :src="authorAvatar(reply.author)!"
                    alt=""
                    class="talk__avatar-img"
                  />
                  <template v-else>{{ initials(reply.author) }}</template>
                </div>
                <div class="talk__meta">
                  <strong>{{ displayName(reply.author) }}</strong>
                  <time>{{ formatTime(reply.created_at) }}</time>
                </div>
              </div>
              <p class="talk__body talk__body--sm">{{ reply.body }}</p>
              <button
                type="button"
                class="talk__action talk__action--sm"
                :class="{ 'talk__action--active': reply.liked_by_me }"
                @click="likeReply(post, reply)"
              >
                <svg viewBox="0 0 24 24" width="14" height="14" :fill="reply.liked_by_me ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="2" aria-hidden="true">
                  <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                {{ toPersianDigits(reply.like_count) }}
              </button>
            </div>

            <form class="talk__reply-form" @submit.prevent="submitReply(post)">
              <input
                v-model="replyDrafts[post.id]"
                type="text"
                maxlength="2000"
                placeholder="پاسخت رو بنویس..."
                required
              />
              <button
                type="submit"
                class="talk__btn talk__btn--primary talk__btn--sm"
                :disabled="replyingId === post.id || !(replyDrafts[post.id] || '').trim()"
              >
                ارسال
              </button>
            </form>
          </div>
        </article>
      </section>
    </div>
  </main>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from "vue";
import { useRouter } from "vue-router";
import {
  createCommunityPost,
  createCommunityReply,
  listCommunityPosts,
  togglePostLike,
  toggleReplyLike,
} from "@/api/community";
import { handleError } from "@/services/errorHandler";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import ErrorMessage from "@/components/common/ErrorMessage.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import type { CommunityAuthor, CommunityPost, CommunityReply } from "@/types";
import { resolveAvatarSrc } from "@/constants/avatars";

const AVATAR_COLORS = [
  { bg: "#f4a97a", color: "#ffffff" },
  { bg: "#6ee7b7", color: "#ffffff" },
  { bg: "#86efac", color: "#ffffff" },
  { bg: "#fca5a5", color: "#ffffff" },
  { bg: "#93c5fd", color: "#ffffff" },
  { bg: "#c4b5fd", color: "#ffffff" },
];

const router = useRouter();
const posts = ref<CommunityPost[]>([]);
const loading = ref(true);
const posting = ref(false);
const replyingId = ref<number | null>(null);
const errorMessage = ref<string | null>(null);
const formError = ref<string | null>(null);
const newPost = ref("");
const expanded = reactive<Record<number, boolean>>({});
const replyDrafts = reactive<Record<number, string>>({});

function toPersianDigits(value: number | string): string {
  return String(value).replace(/\d/g, (digit) => "۰۱۲۳۴۵۶۷۸۹"[Number(digit)]);
}

function displayName(author: CommunityAuthor): string {
  return author.username?.trim() || `کاربر #${author.id}`;
}

function initials(author: CommunityAuthor): string {
  const name = author.username?.trim();
  if (!name) return "؟";
  return name
    .split(/[\s_]+/)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function avatarStyle(userId: number) {
  return AVATAR_COLORS[userId % AVATAR_COLORS.length];
}

function authorAvatar(author: CommunityAuthor): string | null {
  return resolveAvatarSrc(author.avatar_url);
}

function formatTime(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("fa-IR");
}

async function load() {
  loading.value = true;
  errorMessage.value = null;
  try {
    const data = await listCommunityPosts({ page: 1, size: 50 });
    posts.value = data.items || [];
  } catch (e) {
    errorMessage.value = handleError(e, "CommunityTalk.load");
  } finally {
    loading.value = false;
  }
}

async function submitPost() {
  if (posting.value || !newPost.value.trim()) return;
  posting.value = true;
  formError.value = null;
  try {
    const created = await createCommunityPost(newPost.value.trim());
    posts.value = [created, ...posts.value];
    newPost.value = "";
  } catch (e) {
    formError.value = handleError(e, "CommunityTalk.submitPost");
  } finally {
    posting.value = false;
  }
}

function toggleReplies(postId: number) {
  expanded[postId] = !expanded[postId];
}

async function submitReply(post: CommunityPost) {
  const body = (replyDrafts[post.id] || "").trim();
  if (!body || replyingId.value === post.id) return;
  replyingId.value = post.id;
  formError.value = null;
  try {
    const reply = await createCommunityReply(post.id, body);
    post.replies = [...(post.replies || []), reply];
    post.reply_count = post.replies.length;
    replyDrafts[post.id] = "";
    expanded[post.id] = true;
  } catch (e) {
    formError.value = handleError(e, "CommunityTalk.submitReply");
  } finally {
    replyingId.value = null;
  }
}

async function likePost(post: CommunityPost) {
  try {
    const result = await togglePostLike(post.id);
    post.liked_by_me = result.liked;
    post.like_count = result.like_count;
  } catch (e) {
    formError.value = handleError(e, "CommunityTalk.likePost");
  }
}

async function likeReply(post: CommunityPost, reply: CommunityReply) {
  try {
    const result = await toggleReplyLike(reply.id);
    reply.liked_by_me = result.liked;
    reply.like_count = result.like_count;
    const idx = post.replies.findIndex((r) => r.id === reply.id);
    if (idx >= 0) post.replies[idx] = { ...reply };
  } catch (e) {
    formError.value = handleError(e, "CommunityTalk.likeReply");
  }
}

function goBack() {
  if (window.history.state && window.history.state.back) {
    router.back();
  } else {
    router.push("/dashboard");
  }
}

onMounted(load);
</script>

<style scoped>
.talk {
  --color-primary: #6D5EF8;
  --color-secondary: #6A4328;
  --color-bg: #FFFDF8;
  --color-card: #FFFFFF;
  --color-border: #ECE4D9;
  --color-text: #4C372A;
  --color-muted: #8C8176;

  min-height: 100vh;
  background: var(--color-bg);
  color: var(--color-text);
  font-family: Vazirmatn, "Segoe UI", Tahoma, sans-serif;
  padding: 56px 16px 100px;
  direction: rtl;
  box-sizing: border-box;
}

.talk__inner {
  max-width: 720px;
  margin-inline: auto;
}

.talk__back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--color-card);
  border: 1px solid var(--color-border);
  color: var(--color-text);
  font-size: 14px;
  font-weight: 500;
  padding: 9px 18px;
  border-radius: 999px;
  cursor: pointer;
  margin-bottom: 28px;
}

.talk__eyebrow {
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-secondary);
}

.talk__title {
  margin: 0 0 8px;
  font-size: 30px;
  font-weight: 700;
}

.talk__subtitle {
  margin: 0 0 24px;
  color: var(--color-muted);
  font-size: 15px;
  line-height: 1.7;
}

.talk__composer,
.talk__card {
  background: var(--color-card);
  border: 1px solid var(--color-border);
  border-radius: 18px;
  padding: 16px;
  box-shadow: 0 4px 16px rgba(76, 55, 42, 0.05);
}

.talk__composer {
  margin-bottom: 18px;
}

.talk__composer textarea,
.talk__reply-form input {
  width: 100%;
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 12px;
  font: inherit;
  color: var(--color-text);
  background: #fff;
  box-sizing: border-box;
  resize: vertical;
}

.talk__composer textarea:focus,
.talk__reply-form input:focus {
  outline: none;
  border-color: #7C4DFF;
  box-shadow: 0 0 0 3px rgba(124, 77, 255, 0.14);
}

.talk__composer-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}

.talk__btn {
  border: none;
  border-radius: 12px;
  padding: 10px 16px;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.talk__btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.talk__btn--primary {
  background: var(--color-primary);
  color: #fff;
}

.talk__btn--sm {
  padding: 8px 12px;
  font-size: 13px;
}

.talk__error {
  color: #C45C5C;
  font-size: 13px;
  font-weight: 600;
  margin: 0 0 12px;
}

.talk__feed {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.talk__card-head,
.talk__reply-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.talk__avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
  overflow: hidden;
}

.talk__avatar--image {
  background: #FFFDF8 !important;
  color: inherit;
}

.talk__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.talk__avatar--sm {
  width: 30px;
  height: 30px;
  font-size: 11px;
}

.talk__meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.talk__meta strong {
  font-size: 14px;
}

.talk__meta time {
  font-size: 11px;
  color: var(--color-muted);
}

.talk__body {
  margin: 0 0 12px;
  font-size: 15px;
  line-height: 1.8;
  white-space: pre-wrap;
  word-break: break-word;
}

.talk__body--sm {
  font-size: 14px;
  margin-bottom: 8px;
}

.talk__actions {
  display: flex;
  gap: 8px;
}

.talk__action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--color-border);
  background: #FFFDF8;
  color: var(--color-muted);
  border-radius: 999px;
  padding: 6px 12px;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.talk__action--sm {
  padding: 4px 10px;
}

.talk__action--active {
  color: #C45C5C;
  border-color: #F0D2C8;
  background: #FFF7F2;
}

.talk__action--open {
  color: #6D5EF8;
  border-color: #c4b5fd;
  background: #FBF7FC;
}

.talk__replies {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid var(--color-border);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.talk__reply {
  background: #FBF7FC;
  border-radius: 14px;
  padding: 12px;
}

.talk__reply-form {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
}
</style>
